import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part } from '../body';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';
import { domeLight, type Ellipse, type HeadPicture, inEllipse, mask, type SetFlair, toneOf } from './mythic';

/**
 * The Glacier set (ADR-0160 amended, mythic): a frost king, the Inferno's mirror: a crown of ice, rime plate under a fur
 * collar, and the Permafrost gun.
 *
 * The head is a helm of ice cut like a gem (a mask over the whole head): flat facets round a table up and back on it, each
 * lit as one, crisp lines between them (white where they face the light), under a crown of five tall crystals standing on
 * a steel circlet, each white on its lit facet and pale on its shaded one with a dark line down the middle where they
 * meet, two more peeking over the dome behind; a gem of the team's colour in the circlet's front; a dark visor across the
 * face, frosted along its brow with icicles off it, two narrow slits of the team's light in it; a collar of white fur
 * under the chin, in tufts. Lying, the shade between the crystals fills the gaps the dead face would show through. The
 * plate is grey-blue steel rimed white along its top, the team's tabard down the middle, round pauldrons capped in snow
 * over a rim of the team's colour, icicles hanging off them. Its flair is winter: snow falling round it (behind and in
 * front, mostly carried along), shards of ice going round the crown, cyan glints coming and going on its crystals and
 * the pauldrons' snow, a puff of cold breath out of the visor every second and a half, the team's light in the slits
 * breathing, a cold mist; frost footprints left on the run; cold vapour sinking off the gun.
 *
 * Rubric (`assetc mythic` at 5× and 8× and 1:1 on grass, lane and night, both teams, idle, run and dead, beside the
 * Inferno): crown S5 R5 P4 L4 C4 A4 F4, plate S4 R4 P4 L4 C4 F4. Still weak: white and grey-blue, it is quieter than the
 * Inferno's fire on every ground (its crystal crown, the team's slits and the snow carry it); at 1:1 the helm's facets are
 * a pale mottle, the gem cut shows from ×2 up; the plate shows little past the gun (its left pauldron and the collar carry
 * it).
 */

// --- The ice crown ---------------------------------------------------------------------------------------------------

/** Ice by the light on it: a white sheen, pale grey, grey-blue, slate turned away. */
const ICE = [
  ['w', 0.93],
  ['5', 0.62],
  ['4', 0.22],
] as const;
/** One step into the shade along the ice's ramp. */
const DEEPER: Partial<Record<PaletteKey, PaletteKey>> = { w: '5', '5': '4', '4': '3', '3': '2', '2': '1' };
const deeper = (k: PaletteKey): PaletteKey => DEEPER[k] ?? k;
/** White fur by the light on it: a sheen, pale grey, grey-blue in its hollows. */
const FLEECE = [
  ['w', 0.8],
  ['5', 0.3],
  ['4', -0.2],
] as const;
/** The helm from the head's middle (u forward, v down): low, so the crown stands tall over it. */
const HELM: Ellipse = [0.3, 2.5, 22.3, 14.5];
/** The circlet's middle row at `u`: a ring round the helm seen from above and in front, lowest at the brow. */
const circlet = (u: number) => -9.5 + 3 * (1 - ((u - 3) / 20) ** 2);
/** A crystal of the crown: [foot u, foot v (from the circlet for those on it), height, half-width, lean]. */
type Shard = readonly [u: number, v: number, h: number, w: number, lean: number];
/** Behind the helm, peeking over its dome between the front ones. */
const BACK: readonly Shard[] = [
  [-12, -9, 12, 2.8, -0.3],
  [8, -9, 14, 3, 0.1],
];
/** Standing on the circlet, in front: the outer ones first, the tallest in the middle last. */
const FRONT: readonly Shard[] = [
  [-16, 0, 10, 3.2, -0.42],
  [19.5, 0, 9, 3, 0.48],
  [-7.5, 0, 15, 3.9, -0.16],
  [13, 0, 15, 3.9, 0.2],
  [3, 0, 18, 4.9, 0.02],
];

/** Where (u, v) is across `s` (-1 its left edge … 1 its right), how far up it (0 foot … 1 tip); null outside. */
function inShard(u: number, v: number, [fu, fv, h, w, lean]: Shard, foot: number): [number, number] | null {
  const t = (foot + fv - v) / h;
  if (t < -0.3 || t > 1) return null;
  const half = w * (t < 0.55 ? 1 : (1 - t) / 0.45);
  const d = (u - fu - lean * h * Math.max(0, t)) / Math.max(0.01, half);
  return Math.abs(d) < 1 ? [d, t] : null;
}

/**
 * A crystal's face: its left facet in the light, a crisp dark line down its middle where the facets meet, its right facet
 * in the shade, darker at its foot (and behind the helm).
 */
function crystal(d: number, t: number, back: boolean): PaletteKey {
  const face = d < -0.1 ? 'w' : d < 0.12 ? '4' : d < 0.72 ? '5' : '4';
  const lit = back ? deeper(face) : face;
  return t > 0.18 ? lit : deeper(lit);
}

/** The helm cut like a gem round a point up and back on it: a table, then rings of facets, each flat in the light. */
const FACET_AT = [-6, -3] as const;
const RINGS = [0.5, 0.85] as const;
const SECTORS = [1, 7, 11] as const;

/** The helm's ice at (u, v): its facet's tone; along the edges between facets a crisp line, white where it faces the light. */
function facet(u: number, v: number, lx: number, ly: number): PaletteKey {
  const [, , rx, ry] = HELM;
  const [nx, ny] = [(u - FACET_AT[0]) / rx, (v - FACET_AT[1]) / ry];
  const r = Math.hypot(nx, ny);
  const ring = r < RINGS[0] ? 0 : r < RINGS[1] ? 1 : 2;
  const n = SECTORS[ring] as number;
  const k = (Math.atan2(ny, nx) / (2 * Math.PI) + 0.5) * n + ring * 0.5;
  const mid = ((Math.floor(k) + 0.5 - ring * 0.5) / n - 0.5) * 2 * Math.PI;
  const rm = ring === 0 ? 0 : ring === 1 ? (RINGS[0] + RINGS[1]) / 2 : RINGS[1] + 0.25;
  const [fu, fv] = [FACET_AT[0] + Math.cos(mid) * rm * rx, FACET_AT[1] + Math.sin(mid) * rm * ry];
  const tone = toneOf(domeLight(fu, fv, HELM, lx, ly), ICE, '3');
  const px = (rx + ry) / 2;
  const edge =
    (ring > 0 && Math.abs(k - Math.round(k)) * ((2 * Math.PI) / n) * r * px < 0.5) ||
    RINGS.some((b) => Math.abs(r - b) * px < 0.45);
  // The edges facing the light catch it (a frosted sheen), the rest are dark.
  return !edge ? tone : nx + ny < -0.35 && (tone === 'w' || tone === '5') ? 'w' : deeper(tone);
}

/** Under the crown lying, where the dead face would show between its crystals. */
const LYING_FILL: Ellipse = [0.5, -1, 17.5, 23.6];

/** The eye slits: [middle u, middle v, half-width, half-height, slant down to the nose]. */
const SLITS: readonly (readonly [number, number, number, number, number])[] = [
  [-1.5, 2, 4.8, 1.9, 0.18],
  [12, 2, 3.6, 1.8, -0.18],
];
/** The visor's brow and its foot (v). */
const [BROW, CHIN] = [-2.2, 6.5];
/** Icicles hanging off the brow over the visor: [u, length]. */
const DRIPS = [
  [-7, 1],
  [-4, 2],
  [1, 1],
  [8, 2],
  [15, 1],
  [18, 2],
] as const;
/** The fur collar under the chin: a band of white fur, its edges in tufts. */
const COLLAR: Ellipse = [1.5, 15.5, 20, 5.6];
/** Tufts every so many px along the collar. */
const TUFT = 4.2;

/** The collar's fur at (u, v), or null outside it: lit as a roll, a crease between each tuft and the next. */
function fur(u: number, v: number, lx: number, ly: number): PaletteKey | null {
  const k = (u + 40 + (v - COLLAR[1]) * 0.5) / TUFT;
  const across = k - Math.floor(k);
  // Each tuft's tip pokes out past the band's edge, the crease between two tucked in.
  const tip = 1 - Math.abs(across - 0.5) * 2;
  const r = inEllipse(u, v, [COLLAR[0], COLLAR[1] + (v > COLLAR[1] ? 0.9 : -0.4) * tip, COLLAR[2], COLLAR[3]]);
  if (r > 1) return null;
  const l = domeLight(u, v, COLLAR, lx, ly) + (tip - 0.5) * 0.35;
  const tone = toneOf(l, FLEECE, '3');
  // The crease between tufts, slanting as the fur lies.
  return across < 0.16 && v > COLLAR[1] - 2 ? (tone === 'w' ? '5' : tone === '5' ? '4' : '3') : tone;
}

const crown: HeadPicture = (u, v, lx, ly, lying) => {
  // The fur collar in front of all.
  const fleece = fur(u, v, lx, ly);
  if (fleece) return fleece;
  const top = circlet(u);
  // The gem in the crown's front, in the team's colour, set in steel.
  const [gu, gv] = [3, circlet(3) + 0.5];
  const gem = Math.abs(u - gu) / 3.3 + Math.abs(v - gv) / 4.2;
  if (gem < 1) {
    if (gem > 0.78) return '1';
    if (gem < 0.36 && u < gu && v < gv) return 'w';
    return u - gu + (v - gv) < -0.6 ? 'c' : u - gu + (v - gv) > 1.2 ? 'B' : 'b';
  }
  // The crown's crystals standing on the circlet, in front of the helm.
  let shard: [number, number] | null = null;
  for (const s of FRONT) shard = inShard(u, v, s, circlet(s[0]) - 1.2) ?? shard;
  if (shard && v < top - 1.2) return crystal(shard[0], shard[1], false);
  if (inEllipse(u, v, HELM) <= 1) {
    // The circlet: steel, lit along its top.
    if (Math.abs(v - top) < 1.6) return v < top - 0.6 ? '4' : v > top + 0.6 ? '1' : '2';
    if (shard) return crystal(shard[0], shard[1], false);
    // The visor over the face: dark, its brow frosted, icicles off it, two slits of the team's light.
    if (u > -9.5 && v > BROW && v < CHIN) {
      for (const [eu, ev, hw, hh, slant] of SLITS) {
        const du = u - eu;
        const e = (du / hw) ** 2 + ((v - ev - du * slant) / hh) ** 2;
        if (e < 0.32) return 'w';
        if (e < 1) return 'c';
        if (e < 2 && v > ev + du * slant) return 'B';
      }
      if (v < BROW + 0.8 || DRIPS.some(([x, n]) => Math.abs(u - x) < 0.6 && v < BROW + 0.8 + n)) return 'w';
      // A ridge down the middle, between the eyes.
      if (Math.abs(u - 5.4) < 0.6) return '3';
      return u < -7.5 || v < BROW + 1.8 ? '2' : '1';
    }
    return facet(u, v, lx, ly);
  }
  for (const s of BACK) {
    const at = inShard(u, v, s, 0);
    if (at) return crystal(at[0], at[1], true);
  }
  // Lying, the dead face reaches up under the crown: the shade between its crystals fills the gaps over it.
  return lying && inEllipse(u, v, LYING_FILL) <= 1 ? (inEllipse(u, v, LYING_FILL) > 0.8 ? '1' : '2') : null;
};

// --- Rime plate ------------------------------------------------------------------------------------------------------

/**
 * The plate standing (the frame's x 4-57, y 39-59): a breastplate of grey-blue steel frosted white along its top edge,
 * the team's tabard down the middle, round pauldrons capped in snow with icicles hanging off them.
 */
function plate(): Part {
  const [x0, y0] = [4, 39];
  const c = new PixelCanvas(54, 21);
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  for (let y = 44; y <= 55; y++)
    for (let x = 16; x <= 43; x++) at(x, y, x === 16 || y === 45 ? '4' : x >= 41 || y >= 54 ? '2' : '3');
  // Rime along the plate's top, dripping down it.
  for (let x = 16; x <= 43; x++) at(x, 44, 'w');
  for (const [x, n] of [
    [18, 2],
    [21, 1],
    [37, 1],
    [40, 2],
  ] as const)
    for (let k = 1; k <= n; k++) at(x, 44 + k, k === n ? '5' : 'w');
  // The team's tabard down the middle, its hem cut in a point.
  for (let y = 46; y <= 56; y++)
    for (let x = 25; x <= 34; x++) {
      if (y > 53 && Math.abs(x + 0.5 - 30) > 5 - (y - 53) * 1.6) continue;
      at(x, y, x === 25 || y === 46 ? 'c' : x >= 33 || y >= 55 ? 'B' : 'b');
    }
  for (const cx of [11.5, 48]) {
    const [cy, rx, ry] = [47.5, 6.2, 4.8];
    // Where the snow on it ends: a wavy line across its top.
    const snow = (x: number) => cy - 1.2 + Math.sin(x * 1.3 + cx) * 0.8;
    for (let y = 42; y <= 53; y++)
      for (let x = 4; x <= 57; x++) {
        const [px, py] = [x + 0.5, y + 0.5];
        if (inEllipse(px, py, [cx, cy, rx, ry]) > 1) continue;
        const l = -(px - cx) / rx - (py - cy) / ry;
        if (py < snow(px)) at(x, y, l > 0.9 ? 'w' : l > -0.2 ? '5' : '4');
        // Its rim trimmed in the team's colour under the snow.
        else if (inEllipse(px, py, [cx, cy, rx, ry]) > 0.55 && py > cy) at(x, y, l > -0.3 ? 'c' : l > -0.9 ? 'b' : 'B');
        else at(x, y, l > -0.6 ? '3' : '2');
      }
    // Icicles hanging off its rim.
    for (const [dx, n] of [
      [-4, 3],
      [-2, 5],
      [1, 4],
      [3, 2],
    ] as const)
      for (let k = 0; k < n; k++) {
        const x = Math.round(cx + dx);
        at(x, 52 + k, k === n - 1 ? '4' : 'w');
        if (k < n - 2) at(x + 1, 52 + k, '5');
      }
  }
  return [c, x0, y0];
}

/** The plate lying (right of the legs, under the head): the tabard at the waist, the plate, the mantle at the neck. */
const DEAD_PLATE = grid(`
  ....55ww
  ...5w5w5
  c3445w55
  b3344455
  b3344w55
  B3344455
  cbbbbbb5
  bbbbbbb5
  bbbbbbB5
  BBBBBBB5
  b3344455
  b334w455
  b3344455
  B3344455
  b33444w5
  b3344455
  b3333555
  ....4444
`);

/** Frosted greaves and boots under the team's trousers. */
const RIME_PLATE = (): Outfit => ({ torso: plate(), dead: [DEAD_PLATE, 24, 51], legs: { n: '4', N: '2' } });

// --- Permafrost on every other gun -----------------------------------------------------------------------------------

/** The Permafrost finish (the default gun is its own, `pollinator_glacier`): frosted steel, wood turned to pale ice, brass to silver. */
const PERMAFROST = {
  '3': '4',
  '4': '5',
  '5': 'w',
  N: '2',
  n: '3',
  m: '4',
  t: '5',
  e: 'w',
  U: '4',
  Y: '5',
  y: 'w',
} as const;

// --- Its flair -------------------------------------------------------------------------------------------------------

const WHITE = 0xffffff;
const FROST = 0xc0cbdc;
const SLATE = 0x8b9bb4;
const GLINT = 0x2ce8f5;

/** A puff of cold breath out of the visor, then nothing for a second: frames 0.1 s each. */
const BREATH = [
  `
  ............
  ............
  .55.........
  5ww5........
  .554........
  `,
  `
  ............
  ..555.......
  .5www5......
  5www5w5.....
  .5w5554.....
  ..54........
  `,
  `
  ....555.55..
  ..55www5ww5.
  .5ww5w55w54.
  ..5w5554455.
  ...54..4.4..
  `,
  `
  ......5..5..
  ....55...55.
  ..5..5.4..5.
  .....4...4..
  `,
  '.',
  '.',
  '.',
  '.',
  '.',
  '.',
  '.',
  '.',
  '.',
  '.',
] as const;

const FLAIR: SetFlair = {
  head: [
    // The cold round it, breathing.
    {
      kind: 'pulse',
      at: { on: 'head', x: 31, y: 18 },
      r: [20, 26],
      alpha: [0.05, 0.13],
      period: 1.7,
      tone: FROST,
      layer: 'back',
    },
    // Snow falling round it, behind and in front.
    {
      kind: 'emit',
      at: { on: 'head', x: 31, y: -6 },
      box: [70, 10],
      rate: 16,
      v: [-6, 6, 28, 38],
      // Mostly carried along: it falls round it, a little trailing behind a run.
      carry: 0.85,
      g: 30,
      drag: 0.5,
      wobble: [2.5, 0.7],
      life: [0.95, 1.3],
      size: [1, 1],
      tones: [WHITE, FROST],
      layer: 'back',
    },
    {
      kind: 'emit',
      at: { on: 'head', x: 31, y: -6 },
      box: [66, 10],
      rate: 7,
      v: [-6, 6, 28, 38],
      carry: 0.85,
      g: 30,
      drag: 0.5,
      wobble: [3, 0.6],
      life: [0.95, 1.3],
      size: [2, 2],
      tones: [WHITE, WHITE, FROST],
      layer: 'front',
    },
    // Shards of ice going round the crown: over its foot on the near side, out past the head on either.
    {
      kind: 'orbit',
      at: { on: 'head', x: 31, y: 15 },
      rx: 27,
      ry: 5,
      tilt: -0.08,
      n: 3,
      period: 2.4,
      size: 2,
      tones: [FROST, WHITE, GLINT],
      flame: [8, 1.7],
      glow: 4,
    },
    // Glints coming and going on its crystals: cyan stars on the white.
    {
      kind: 'twinkle',
      at: { on: 'head', x: 31, y: 11 },
      box: [40, 16],
      n: 9,
      period: [0.45, 1],
      tones: [GLINT, WHITE, GLINT],
      layer: 'front',
    },
    // Its eyes in the team's light.
    {
      kind: 'pulse',
      at: { on: 'head', x: 28.5, y: 30 },
      r: [4, 6.5],
      alpha: [0.35, 0.62],
      period: 1.3,
      tone: 'teamLight',
      layer: 'front',
    },
    {
      kind: 'pulse',
      at: { on: 'head', x: 42, y: 30 },
      r: [3.5, 5.5],
      alpha: [0.35, 0.62],
      period: 1.3,
      tone: 'teamLight',
      layer: 'front',
    },
    // Cold breath out of the visor.
    {
      kind: 'glyph',
      at: { on: 'head', x: 50, y: 29 },
      frames: BREATH,
      tones: { w: WHITE, '5': FROST, '4': SLATE },
      hold: [0.09, 0.12],
      layer: 'front',
    },
  ],
  outfit: [
    // Cyan glints on the pauldrons' snow.
    {
      kind: 'twinkle',
      at: { on: 'torso', x: 11, y: 46 },
      box: [12, 7],
      n: 3,
      period: [0.5, 1.1],
      tones: [GLINT, WHITE],
      layer: 'front',
    },
    {
      kind: 'twinkle',
      at: { on: 'torso', x: 48, y: 46 },
      box: [12, 7],
      n: 2,
      period: [0.5, 1.1],
      tones: [GLINT, WHITE],
      layer: 'front',
    },
    // Frost footprints left on the run.
    {
      kind: 'emit',
      at: { on: 'feet', x: 30, y: 69 },
      box: [12, 2],
      rate: 0,
      run: 0.05,
      v: [0, 0, 0, 0],
      life: [0.7, 1.1],
      size: [3, 2],
      tones: [WHITE, FROST, SLATE],
      alpha: [0.95, 0],
      layer: 'back',
    },
  ],
  gun: [
    // Cold vapour sinking off the crystal and the muzzle.
    {
      kind: 'emit',
      at: { on: 'gun', x: 46, y: 22 },
      box: [24, 4],
      rate: 7,
      v: [-4, 4, 4, 10],
      g: 12,
      drag: 0.5,
      wobble: [1.5, 1],
      life: [0.6, 1.1],
      size: [2, 3],
      tones: [FROST, SLATE],
      alpha: [0.7, 0],
      layer: 'gun',
    },
    {
      kind: 'twinkle',
      at: { on: 'gun', x: 35, y: 8 },
      box: [22, 10],
      n: 3,
      period: [0.6, 1.2],
      tones: [GLINT, WHITE],
      layer: 'gun',
    },
  ],
};

/** The Glacier set (ADR-0160 amended). */
export const GLACIER = drawnLater({ head: () => mask(crown), outfit: RIME_PLATE, gun: PERMAFROST, flair: FLAIR });
